import React, { memo, useCallback } from 'react';

import { Box, Link, Typography } from '@mui/material';
import { DocumentIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { openExternalUrl } from 'utils/nativeFile';
import { getIsNativePlatform } from 'utils/platform';

import { TEAM_CHAT_IMAGE_CONTENT_TYPE_PREFIX } from '../TeamChat.constants';
import { formatFileSize } from '../TeamChat.utils';

import {
  fileIconStyles,
  fileLinkStyles,
  fileMetaStyles,
  fileRowStyles,
  imageStyles,
  rootStyles,
} from './TeamChatFileMessage.styles';
import { TeamChatFileMessageTestIds } from './TeamChatFileMessage.testIds';

const TeamChatFileMessage = memo(({ file }) => {
  const { fileName, blobUrl, contentType, sizeBytes } = file;
  const isImage = !!contentType && contentType.startsWith(TEAM_CHAT_IMAGE_CONTENT_TYPE_PREFIX);

  const onOpenAttachment = useCallback(
    e => {
      if (getIsNativePlatform()) {
        e.preventDefault();
        openExternalUrl(blobUrl);
      }
    },
    [blobUrl],
  );

  return (
    <Box sx={rootStyles}>
      {isImage && (
        <Link
          data-testid={TeamChatFileMessageTestIds.IMAGE_LINK}
          href={blobUrl}
          rel="noopener noreferrer"
          target="_blank"
          underline="none"
          onClick={onOpenAttachment}
        >
          <Box alt={fileName} component="img" src={blobUrl} sx={imageStyles} />
        </Link>
      )}
      <Box sx={fileRowStyles}>
        <Box sx={fileIconStyles}>
          <DocumentIcon size={IconSize[20]} />
        </Box>
        <Box>
          <Link
            data-testid={TeamChatFileMessageTestIds.FILE_LINK}
            href={blobUrl}
            rel="noopener noreferrer"
            sx={fileLinkStyles}
            target="_blank"
            underline="hover"
            onClick={onOpenAttachment}
          >
            <Typography variant="medium14">{fileName || 'Attachment'}</Typography>
          </Link>
          {!!sizeBytes && (
            <Typography sx={fileMetaStyles} variant="regular12">
              {formatFileSize(sizeBytes)}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
});
TeamChatFileMessage.propTypes = {
  file: PropTypes.shape({
    fileName: PropTypes.string,
    blobUrl: PropTypes.string,
    contentType: PropTypes.string,
    sizeBytes: PropTypes.number,
  }).isRequired,
};

export default TeamChatFileMessage;
